import { ButtonProps, CSSProperties, CardSection } from '@mantine/core';
import { IconCheck, IconWritingSign } from '@tabler/icons-react';
import { getFullName } from '@zorro/common-utils';
import { Button, ButtonVariant } from '@zorro/design-system/v2/Button';
import { Card } from '@zorro/design-system/v2/Card';
import { Divider } from '@zorro/design-system/v2/Divider';
import { TextInput } from '@zorro/design-system/v2/TextInput';
import {
  Box,
  Flex,
  FormErrorMessage,
  Group,
  Icon,
  Stack,
  Text,
} from '@zorro/web/design-system';
import { useMonolithQuery } from '@zorro/web/ui-utils';
import { ElementType, useState } from 'react';
import { Controller } from 'react-hook-form';
import ReactMarkdown from 'react-markdown';
import { $enum } from 'ts-enum-util';

import { useAnalytics } from '../../Analytics';
import {
  CarrierAttestations,
  consentIntroMarkdown,
  medicalElectionsConsentMarkdown,
} from '../../ConsentContent';
import {
  SignButtonState,
  useManualElectionConsent,
} from './useManualElectionConsent';

type Config = {
  label: string;
  disabled: boolean;
  style?: CSSProperties;
  leftSection?: ButtonProps['leftSection'];
  icon?: ElementType;
  variant?: ButtonVariant;
  bg?: string;
};

const getSignButtonConfig = (state: SignButtonState) => {
  return $enum.mapValue(state).with<Config>({
    [SignButtonState.INVALID]: {
      label: 'Sign',
      leftSection: undefined,
      disabled: true,
      bg: undefined,
    },
    [SignButtonState.VALID]: {
      disabled: false,
      leftSection: undefined,
      label: 'Sign',
    },
    [SignButtonState.SUBMITTED]: {
      label: 'Signed',
      leftSection: <Icon icon={IconCheck} />,
      variant: 'positive',
      disabled: false,
    },
  });
};

// The input plus one FormErrorMessage line, so the card keeps its height when the message appears or the name is signed
const SIGNATURE_ROW_MIN_HEIGHT =
  'calc(var(--input-height-md) + var(--mantine-font-size-sm) * var(--mantine-line-height-sm) + 2 * var(--mantine-spacing-xs))';

type Props = {
  employeeId: string;
  consent: ReturnType<typeof useManualElectionConsent>;
};

export const ManualElectionLegalConsent = ({ employeeId, consent }: Props) => {
  const { sendEvent } = useAnalytics();
  const [showSignature, setShowSignature] = useState(true);
  const { form, signStatus, setSignStatus, reviewSnapshot } = consent;
  const { control, formState } = form;
  const { isValid, errors } = formState;

  const { data: employee } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: [employeeId],
  });

  if (!employee || !reviewSnapshot) {
    return null;
  }

  const signButtonConfig = getSignButtonConfig(
    isValid ? signStatus : SignButtonState.INVALID
  );

  return (
    <Stack gap="md">
      <Stack gap="0.25rem">
        <Text fw={600}>Legal consent</Text>
        <Text size="sm" c="zorroGray.4">
          I have this employee's written consent to select and sign this
          election on their behalf under Zorro's elections consent.
        </Text>
      </Stack>
      <Card p="1rem" variant="white-border">
        <CardSection
          p="1rem 1.5rem"
          withBorder
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <Group gap="0.5rem" align="center">
            <Icon icon={IconWritingSign} size={20} />
            <Text fw={600}>Employee&apos;s medical elections consent</Text>
          </Group>
        </CardSection>
        <CardSection p="1rem 1.5rem 0">
          <Box
            w="100%"
            h="18rem"
            style={{
              overflowY: 'scroll',
              maxHeight: '18rem',
              fontSize: '0.875rem',
            }}
            pb="1rem"
          >
            <ReactMarkdown>{consentIntroMarkdown}</ReactMarkdown>
            <ReactMarkdown>{medicalElectionsConsentMarkdown}</ReactMarkdown>
            <CarrierAttestations
              attestationParagraphs={
                reviewSnapshot.payload.attestationParagraphs ?? null
              }
            />
          </Box>
          <Divider />
          <Flex
            pt="1.5rem"
            pb="0.5rem"
            justify="space-between"
            align="baseline"
            gap="md"
          >
            <Text fz="0.875rem" c="zorroGray.4">
              Employee full legal name: {getFullName(employee)}
            </Text>
            <Flex align="flex-start" gap="md" mih={SIGNATURE_ROW_MIN_HEIGHT}>
              {showSignature ? (
                <Stack gap={0} w="18rem">
                  <Controller
                    control={control}
                    name="signedName"
                    render={({ field }) => (
                      <TextInput
                        {...field}
                        placeholder="Type employee's full legal name"
                        size="md"
                        isMasked
                      />
                    )}
                  />
                  <FormErrorMessage errors={errors} fieldName="signedName" />
                </Stack>
              ) : (
                <Button
                  variant="tertiary"
                  size="md"
                  onClick={() => {
                    setSignStatus(SignButtonState.VALID);
                    setShowSignature(true);
                    void sendEvent('edit_signature_button_click');
                  }}
                >
                  Edit signature
                </Button>
              )}
              <Button
                {...signButtonConfig}
                size="md"
                onClick={() => {
                  if (signStatus === SignButtonState.SUBMITTED) {
                    return;
                  }
                  setSignStatus(SignButtonState.SUBMITTED);
                  setShowSignature(false);
                  void sendEvent('sign_button_click');
                }}
              >
                {signButtonConfig.label}
              </Button>
            </Flex>
          </Flex>
        </CardSection>
      </Card>
    </Stack>
  );
};
